import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Wallet, ArrowLeft, Download, ArrowRight } from "lucide-react";
import { api, inr, fmtDate, apiError } from "@/lib/api";
import { Skeleton, StatusBadge, Progress, EmptyState } from "@/components/common";
import { EMISchedule } from "@/components/EMISchedule";
import { generateStatementPdf } from "@/lib/statementPdf";
import { useT } from "@/context/i18n";

export function Loans() {
  const [loans, setLoans] = useState(null);
  const t = useT();
  const nvg = useNavigate();
  useEffect(() => { api.get("/loans").then((r) => setLoans(r.data)).catch(() => setLoans([])); }, []);
  if (!loans) return <div className="grid gap-4 md:grid-cols-2">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-52" />)}</div>;
  return (
    <div className="space-y-5">
      <div><h1 className="font-display text-2xl font-bold text-[#0B132B]">{t("My Loans")}</h1><p className="text-sm text-slate-500">{t("All your loans across lenders in one place.")}</p></div>
      {loans.length > 0 && (
        <div className="flex items-center justify-between overflow-hidden rounded-2xl bg-gradient-to-br from-[#1F7A54] to-[#0F2E20] p-5 text-white" data-testid="loans-outstanding-hero">
          <div>
            <p className="text-sm text-slate-300">{t("Total Outstanding")}</p>
            <p className="font-display text-3xl font-bold">{inr(loans.reduce((s, l) => s + (l.outstanding || 0), 0))}</p>
            <p className="mt-1 max-w-sm text-xs text-slate-400">{t("Across")} {loans.length} {t("loan(s)")} • {t("Pay through ZRo Pe and earn wallet rewards on confirmed payments.")}</p>
          </div>
          <img src="/brand/zrope-penguin.png" alt="ZRo Pe" className="h-28 w-28 shrink-0 object-contain drop-shadow-xl" data-testid="loans-penguin" />
        </div>
      )}
      {loans.length === 0 ? <EmptyState icon={Wallet} title={t("No loans yet")} desc={t("Use Loan Lookup to link your loans and start tracking EMIs.")} action={<Link to="/link-loan" className="rounded-md bg-[#14B8A6] px-4 py-2 text-sm font-semibold text-white">{t("Check my loan")}</Link>} /> : (
        <div className="grid gap-4 md:grid-cols-2">
          {loans.map((l) => {
            const prog = Math.round(((l.tenure_months - l.remaining_emi) / l.tenure_months) * 100);
            return (
              <div key={l.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between"><div><p className="font-display font-bold text-[#0B132B]">{l.lender}</p><p className="text-xs text-slate-500">{l.loan_type}</p></div><StatusBadge status={l.status} /></div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div><p className="text-xs text-slate-400">{t("EMI")}</p><p className="font-semibold text-[#0B132B]">{inr(l.emi_amount)}/mo</p></div>
                  <div><p className="text-xs text-slate-400">{t("Outstanding")}</p><p className="font-semibold text-[#0B132B]">{inr(l.outstanding)}</p></div>
                  <div><p className="text-xs text-slate-400">{t("Interest")}</p><p className="font-semibold text-[#0B132B]">{l.interest_rate}%</p></div>
                  <div><p className="text-xs text-slate-400">{t("Next Due")}</p><p className="font-semibold text-[#0B132B]">{fmtDate(l.next_due_date)}</p></div>
                </div>
                <div className="mt-4"><Progress value={prog} /><p className="mt-1 text-xs text-slate-500">{prog}% paid • {l.remaining_emi} EMIs left</p></div>
                <div className="mt-4 flex gap-2">
                  {l.status !== "CLOSED" && <button onClick={() => nvg("/payment", { state: { loan: l } })} className="flex-1 rounded-md bg-[#14B8A6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0D9488]" data-testid={`loans-pay-${l.id}`}>{t("Pay EMI")}</button>}
                  <Link to={`/loans/${l.id}`} className="flex flex-1 items-center justify-center gap-1 rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-[#0B132B] hover:bg-slate-50">{t("Details")} <ArrowRight size={14} /></Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function LoanDetail() {
  const { id } = useParams();
  const [l, setL] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [dl, setDl] = useState(false);
  const nvg = useNavigate();
  const t = useT();
  useEffect(() => {
    api.get(`/loans/${id}`).then((r) => setL(r.data)).catch(() => toast.error("Loan not found"));
    api.get(`/loans/${id}/schedule`).then((r) => setSchedule(r.data.schedule)).catch(() => setSchedule([]));
  }, [id]);
  if (!l) return <div className="space-y-4"><Skeleton className="h-8 w-40" /><Skeleton className="h-40 w-full" /></div>;
  const paid = l.tenure_months - l.remaining_emi;
  const prog = Math.round((paid / l.tenure_months) * 100);
  const statement = async () => {
    setDl(true);
    try {
      const { data } = await api.get(`/loans/${id}/statement`);
      await generateStatementPdf(data);
      toast.success("Statement PDF downloaded successfully.");
    } catch (e) { toast.error(apiError(e)); } finally { setDl(false); }
  };
  return (
    <div className="space-y-6">
      <Link to="/loans" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-[#0B132B]"><ArrowLeft size={15} /> {t("Back to loans")}</Link>
      <div className="rounded-2xl bg-gradient-to-br from-[#1F7A54] to-[#0F2E20] p-6 text-white">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div><p className="font-display text-2xl font-bold">{l.lender}</p><p className="text-sm text-slate-300">{l.loan_type}</p></div>
          <StatusBadge status={l.status} />
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[["Loan Amount", inr(l.principal)], ["Outstanding", inr(l.outstanding)], ["EMI", inr(l.emi_amount)], ["Interest", `${l.interest_rate}%`]].map(([a, b]) => (
            <div key={a}><p className="text-xs text-slate-400">{t(a)}</p><p className="font-display text-lg font-bold">{b}</p></div>
          ))}
        </div>
        <div className="mt-6"><div className="mb-1 flex justify-between text-xs text-slate-300"><span>{prog}% repaid</span><span>{paid}/{l.tenure_months} EMIs</span></div><div className="h-2 w-full rounded-full bg-white/10"><div className="h-full rounded-full bg-[#14B8A6]" style={{ width: `${prog}%` }} /></div></div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {[["Start Date", fmtDate(l.start_date)], ["End Date", fmtDate(l.end_date)], ["Next Due", fmtDate(l.next_due_date)]].map(([a, b]) => (
          <div key={a} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"><p className="text-xs text-slate-400">{t(a)}</p><p className="mt-1 font-semibold text-[#0B132B]">{b}</p></div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3">
        {l.status !== "CLOSED" && <button onClick={() => nvg("/payment", { state: { loan: l } })} className="rounded-md bg-[#14B8A6] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0D9488]" data-testid="detail-pay">{t("Pay EMI")} {inr(l.emi_amount)}</button>}
        <button onClick={statement} disabled={dl} data-testid="download-statement" className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-[#0B132B] hover:bg-slate-50 disabled:opacity-60"><Download size={16} /> {dl ? t("Preparing PDF…") : t("Download Statement")}</button>
      </div>

      <EMISchedule schedule={schedule} />

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="font-display font-bold text-[#0B132B]">{t("Payment History")}</h3>
        <div className="mt-3 divide-y divide-slate-100">
          {(!l.payments || l.payments.length === 0) ? <p className="py-6 text-center text-sm text-slate-400">{t("No payments recorded for this loan yet.")}</p> : l.payments.map((p) => (
            <div key={p.id} className="flex items-center justify-between py-3 text-sm"><div><p className="font-medium text-[#0B132B]">{fmtDate(p.created_at)}</p><p className="text-xs text-slate-400">{p.txn_id}</p></div><div className="text-right"><p className="font-semibold text-[#0B132B]">{inr(p.amount)}</p><StatusBadge status={p.status} /></div></div>
          ))}
        </div>
      </div>
    </div>
  );
}
